import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { getPress } from "@/lib/api";

const TYPES = ["All", "Television", "Book", "Publication", "Interview", "Social", "Other"];

export default function Press() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("All");

  useEffect(() => {
    getPress(false).then(setItems).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (type === "All" ? items : items.filter((i) => i.type === type)),
    [items, type]
  );

  return (
    <>
      <SEOHead
        title="Colin Darke Press & Media | Good Morning America, Ripley's & More"
        description="Selected press coverage of Colin Darke, The Ambidextrous Artist — including Good Morning America, Ripley's Believe It or Not!, Henry Ford Health and The Detroit News."
        path="/press"
      />

      <section className="pt-16 pb-8 border-b border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <p className="catalog-label mb-4">Press &amp; media</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
            Where his work
            <br />
            <span className="italic text-[hsl(var(--vermillion))]">
              has been seen.
            </span>
          </h1>
        </div>
      </section>

      <section className="sticky top-20 z-30 bg-[hsl(var(--bg))]/95 backdrop-blur border-b border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 py-4 flex gap-2 overflow-x-auto">
          {TYPES.map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              data-testid={`press-filter-${t.toLowerCase()}`}
              className={`whitespace-nowrap px-4 py-2 font-mono text-xs uppercase tracking-widest border transition-colors ${
                type === t
                  ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))] border-[hsl(var(--ink))]"
                  : "border-[hsl(var(--line))] hover:border-[hsl(var(--ink))]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filtered.map((p) => (
            <a
              key={p.id}
              href={p.source_url || "#"}
              target={p.source_url ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="group flex flex-col sm:flex-row gap-5 bg-[hsl(var(--surface))] border border-[hsl(var(--line))] p-6 hover:border-[hsl(var(--ink))] transition-colors"
              data-testid={`press-item-${p.id}`}
            >
              {p.thumbnail ? (
                <img
                  src={p.thumbnail}
                  alt={p.headline}
                  className="w-full sm:w-40 h-32 sm:h-40 object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-full sm:w-40 h-32 sm:h-40 bg-[hsl(var(--bg-secondary))] flex items-center justify-center flex-shrink-0">
                  <span className="catalog-label">{p.type}</span>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  <span className="catalog-label">{p.publication}</span>
                  {p.date && (
                    <span className="catalog-label text-[hsl(var(--ink-muted))]">
                      {p.date}
                    </span>
                  )}
                </div>
                <h3 className="font-serif text-2xl leading-tight mt-2">
                  {p.headline}
                </h3>
                <p className="text-sm text-[hsl(var(--ink-muted))] mt-2">
                  {p.description}
                </p>
                {p.quote && (
                  <p className="mt-3 pl-3 border-l-2 border-[hsl(var(--vermillion))] italic text-sm">
                    "{p.quote}"
                  </p>
                )}
                {!p.verified && (
                  <p className="mt-3 catalog-label text-[hsl(var(--vermillion))]">
                    Verification / source needed
                  </p>
                )}
                {p.source_url && (
                  <div className="mt-4 inline-flex items-center gap-2 catalog-label group-hover:text-[hsl(var(--vermillion))]">
                    Read the source <ArrowUpRight size={12} />
                  </div>
                )}
              </div>
            </a>
          ))}
        </div>
      </section>
    </>
  );
}
